// Copyright 2019 Sorama B.V.

import { ExponentialSmoother } from "algorithms/ExponentialSmoother";
import { observable } from "aurelia-binding";
import { FrameGenerator, FramePlayer } from "controls/griddataplayer/GridDataPlayer";
import { DataOrder, Grid, GridData } from "controls/plot/grid-plot/GridData";
import { BeamformingProjectionSet, BeamformingProjectionSetProjection, VideoFrame } from "datatypes/SoramaWebApi";
import { ArrayExtensions } from "extensions/ArrayExtensions";
import { IDataExporter, Set } from "libraries/Export/IDataExporter";
import { FileDownloader } from "libraries/FileDownloader/FileDownloader";
import { Length } from "units/Length";
import { Pressure } from "units/Pressure";
import { Time } from "units/Time";
import { Unit, UnitValue } from "units/Unit";

interface FarfieldProjectionContext {
    currentDataIndex?: number;
    currentBackgroundIndex?: number;

    data: GridData;
    background: string;
}

export class FarfieldProjectionModel implements FrameGenerator<FarfieldProjectionContext> {
    public readonly keyUnit: Unit = Time.second;
    public readonly minKey: UnitValue;
    public readonly maxKey: UnitValue;

    @observable
    public videoOffset: UnitValue = new UnitValue(0, Time.second);

    public readonly smoother = new ExponentialSmoother();

    private player: FramePlayer;

    private readonly grid: Grid;
    private readonly distance: number;
    private readonly beamformingFrames: BeamformingProjectionSetProjection[];
    private readonly videoFrames: VideoFrame[];

    private readonly context: FarfieldProjectionContext;

    constructor(
        grid: Grid,
        distance: number,
        beamformingFrames: BeamformingProjectionSet,
        videoFrames?: VideoFrame[],
        videoOffset?: UnitValue
    ) {
        this.grid = grid;
        this.distance = distance;
        this.videoOffset = videoOffset;
        this.beamformingFrames = beamformingFrames.results.sort((a, b) => this.getKeyTime(a) - this.getKeyTime(b));
        this.videoFrames = videoFrames ? videoFrames.sort((a, b) => a.time - b.time) : [];

        this.minKey = new UnitValue(this.getKeyTime(this.beamformingFrames[0]), Time.second);
        this.maxKey = new UnitValue(
            this.getKeyTime(this.beamformingFrames[this.beamformingFrames.length - 1]),
            Time.second
        );

        if (this.videoFrames && this.videoFrames.length > 0) {
            this.minKey = new UnitValue(Math.min(this.minKey.value, this.videoFrames[0].time), Time.second);
            this.maxKey = new UnitValue(
                Math.max(this.maxKey.value, this.videoFrames[this.videoFrames.length - 1].time),
                Time.second
            );
        }

        this.context = { data: undefined, background: undefined };
    }

    public initialize(player: FramePlayer): FarfieldProjectionContext {
        this.player = player;
        this.generate(this.minKey, this.context);
        return this.context;
    }

    public generate(key: UnitValue, frameContext: FarfieldProjectionContext): void {
        // Find frame closest to the key time for both audio and video
        const keyTimeSeconds = key.toUnit(Time.second);
        const dataIndex = ArrayExtensions.fastFindClosestValueIndex(this.beamformingFrames, keyTimeSeconds, (x) =>
            this.getKeyTime(x)
        );
        const videoIndex = ArrayExtensions.fastFindClosestValueIndex(
            this.videoFrames,
            keyTimeSeconds + this.videoOffset.toUnit(Time.second),
            (x) => x.time
        );

        if (frameContext.currentDataIndex === undefined || frameContext.currentDataIndex !== dataIndex) {
            const frame = this.beamformingFrames[dataIndex];
            const displayData = this.smoother.smooth(frame.data, this.getKeyTime(frame));
            frameContext.data = new GridData(
                displayData,
                Pressure.pascal,
                DataOrder.LeftToRightThenTopToBottom,
                this.grid
            );
            frameContext.currentDataIndex = dataIndex;
        }

        if (videoIndex === -1) {
            frameContext.background = undefined;
            frameContext.currentBackgroundIndex = undefined;
        } else if (
            frameContext.currentBackgroundIndex === undefined ||
            frameContext.currentBackgroundIndex !== videoIndex
        ) {
            frameContext.background = "data:image/jpg;base64," + this.videoFrames[videoIndex].data;
            frameContext.currentBackgroundIndex = videoIndex;
        }
    }

    public exportData(exporter: IDataExporter, fileName: string): void {
        // Generate sets
        const xSet: Set = {
            label: "x",
            data: new Array(this.grid.dimension.x * this.grid.dimension.y),
            yUnit: Length.meter,
        };
        const ySet: Set = {
            label: "y",
            data: new Array(this.grid.dimension.x * this.grid.dimension.y),
            yUnit: Length.meter,
        };
        const zSet: Set = {
            label: "z",
            data: new Array(this.grid.dimension.x * this.grid.dimension.y),
            yUnit: Length.meter,
        };
        const data: Set = {
            label: "Projection",
            data: new Array(this.grid.dimension.x * this.grid.dimension.y),
            yUnit: Pressure.pascal,
        };

        let i = 0;

        const zValue = this.distance;
        for (let y = 0; y < this.grid.dimension.y; y++) {
            const yValue = this.grid.spacing.y * (y - this.grid.dimension.y * 0.5 + 0.5) + this.grid.center.y;
            for (let x = 0; x < this.grid.dimension.x; x++, i++) {
                const xValue = this.grid.spacing.x * (x - this.grid.dimension.x * 0.5 + 0.5) + this.grid.center.x;
                xSet.data[i] = xValue;
                ySet.data[i] = yValue;
                zSet.data[i] = zValue;

                const p = this.context.data.data[i];

                data.data[i] = p.toFixed(8);
            }
        }

        const csv = exporter.exportSets([xSet, ySet, zSet, data]);
        FileDownloader.downloadAsFile(csv, "text/plain", fileName + ".csv");
    }

    public videoOffsetChanged(): void {
        if (!this.player) {
            return;
        }

        this.generate(this.player.currentKey, this.context);
    }

    private getKeyTime(projection: BeamformingProjectionSetProjection): number {
        return (projection.properties.timeInterval.from + projection.properties.timeInterval.to) / 2;
    }
}
